import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  IconButton,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// We'll use dynamic imports for jsPDF and html2canvas to avoid build issues
// The actual imports will happen in the generatePDF function

import { useSavedProperties, Property } from '../hooks/useSavedProperties';

// Import useAuth hook to get user ID if not passed as prop
import { useAuth } from '../hooks/useAuth';

// Styled markdown container
const MarkdownContainer = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ 
    '& p': { my: 1 },
    '& ul, & ol': { pl: 2 },
    '& li': { mb: 0.5 },
    '& a': { color: 'primary.main' },
    '& code': { 
      backgroundColor: 'rgba(0, 0, 0, 0.05)', 
      padding: '2px 4px', 
      borderRadius: 1,
      fontFamily: 'monospace'
    },
    '& pre': { 
      backgroundColor: 'rgba(0, 0, 0, 0.05)', 
      padding: 1.5, 
      borderRadius: 1,
      overflow: 'auto'
    },
    '& blockquote': {
      borderLeft: '4px solid',
      borderColor: 'divider',
      pl: 2,
      ml: 0,
      my: 1
    }
  }}>
    {children}
  </Box>
);

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
}

interface BidRequestToolProps {
  userId?: string;
}

const projectTypes = [
  'Roof Replacement',
  'Gutter Installation/Replacement',
  'Vent Screening',
  'Deck/Patio Hardening',
  'Window Replacement',
  'Siding Replacement',
  'Landscaping/Vegetation Management',
  'Driveway/Access Improvement',
  'Other'
];

const BidRequestTool: React.FC<BidRequestToolProps> = ({ userId: propUserId }) => {
  const { user } = useAuth();
  const userId = propUserId || (user ? user.id : undefined);
  
  // Use useRef to track if this is the first render
  const isFirstRender = useRef(true);
  
  // Only log user ID on first render or when it changes
  useEffect(() => {
    if (isFirstRender.current) {
      console.log('BidRequestTool initial userId:', userId);
      isFirstRender.current = false;
    }
  }, []);
  
  // Only log when userId actually changes, not on every render
  const prevUserIdRef = useRef(userId);
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      console.log('BidRequestTool userId changed to:', userId);
      prevUserIdRef.current = userId;
    }
  }, [userId]);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: "# Welcome to the SafeHaven Bid Request Tool\n\nI'm here to help you create a detailed bid request for your home hardening project to protect against wildfires. Let's start by gathering some key information about your project.\n\nPlease tell me:\n\n1. What specific home hardening project are you looking to have done? (e.g., roof replacement, vent screening, landscaping)\n2. What is your approximate budget for this project?\n3. What is your desired timeline for completion?\n4. Can you describe any specific requirements or concerns you have?\n\nAlternatively, you can select a project type from the dropdown above, and I'll guide you through the process step by step."
    }
  ]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [projectType, setProjectType] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<'view' | 'edit'>('view');
  
  // Fetch saved properties
  const { properties, loading: propertiesLoading } = useSavedProperties(userId || '');
  
  // Debug: Log to console to check if properties are loaded
  const prevPropertiesRef = useRef<Property[]>([]);
  useEffect(() => {
    if (JSON.stringify(prevPropertiesRef.current) !== JSON.stringify(properties)) {
      console.log('Saved Properties updated:', properties.length > 0 ? `${properties.length} properties found` : 'No properties found');
      prevPropertiesRef.current = properties;
    }
  }, [properties]);
  
  // Editable fields state
  const [editableFields, setEditableFields] = useState({
    projectOverview: '',
    budget: '',
    timeline: '',
    propertyDetails: '',
    projectRequirements: '',
    specificRequirements: [] as string[],
    contactInfo: ''
  });
  
  const chatEndRef = useRef<HTMLDivElement>(null);
  const pdfContentRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  const handleProjectTypeChange = (event: SelectChangeEvent) => {
    const selectedType = event.target.value;
    setProjectType(selectedType);
    
    if (selectedType) {
      setEditableFields(prev => ({ 
        ...prev, 
        projectOverview: selectedType 
      }));
      
      // Add a message to the chat indicating the selection
      const newMessages = [
        ...chatMessages,
        {
          role: 'assistant' as 'assistant',
          content: `Great, I've selected **${selectedType}** as your project type. Now, could you tell me:\n\n1. What is your approximate budget for this project?\n2. What is your desired timeline for completion?\n3. Do you have any specific requirements or concerns for this ${selectedType.toLowerCase()} project?`
        }
      ];
      setChatMessages(newMessages);
    }
  };

  const handlePropertyChange = (event: SelectChangeEvent) => {
    const propertyId = event.target.value;
    setSelectedProperty(propertyId);
    
    if (propertyId) {
      const selectedProp: Property | undefined = properties.find((p: Property) => p.id === propertyId);
      if (selectedProp) {
        setEditableFields(prev => ({ 
          ...prev, 
          propertyDetails: selectedProp.address + (selectedProp.details ? ` - ${selectedProp.details}` : '')
        }));
        
        // Add a message to the chat indicating the property selection
        const newMessages = [
          ...chatMessages,
          {
            role: 'assistant' as 'assistant',
            content: `I've noted that you want to create a bid request for the property at **${selectedProp.address}**. Is there anything specific about this property that contractors should know?`
          }
        ];
        setChatMessages(newMessages);
      }
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;
    
    setIsLoading(true);
    
    // Add user message to chat
    const userMessage: ChatMessage = {
      role: 'user',
      content: message
    };
    const updatedMessages = [...chatMessages, userMessage];
    setChatMessages(updatedMessages);
    setUserInput('');
    
    // Here you would call your AI service to get a response
    // For now, we'll simulate a response
    try {
      // Simulate API call to AI service
      // In a real app, this would be an actual API request
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Analyze the user's message to extract information
      const analysis = analyzeUserMessage(message);
      
      // Update editable fields based on the analysis
      setEditableFields(prev => ({ 
        ...prev,
        ...analysis.updatedFields
      }));
      
      // Generate an AI response
      const aiResponse = generateAIResponse(analysis);
      
      // Add AI response to chat
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: aiResponse
      };
      setChatMessages([...updatedMessages, assistantMessage]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error. Could you please rephrase your message or provide more details about your project?"
      };
      setChatMessages([...updatedMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to analyze user message and extract relevant information
  const analyzeUserMessage = (message: string) => {
    const lowerMessage = message.toLowerCase();
    const updatedFields: Partial<typeof editableFields> = {};
    let extractedInfo = [];
    
    // Check for budget information
    if (lowerMessage.includes('budget') || lowerMessage.includes('$') || lowerMessage.match(/\d{3,}/)) {
      const budgetMatch = lowerMessage.match(/\$?(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/);
      if (budgetMatch) {
        updatedFields.budget = budgetMatch[0];
        extractedInfo.push(`Budget: ${budgetMatch[0]}`);
      }
    }
    
    // Check for timeline information
    if (lowerMessage.includes('time') || lowerMessage.includes('complete') || lowerMessage.includes('finish') || lowerMessage.includes('done')) {
      const timelineKeywords = ['asap', 'soon', 'urgent', 'immediate', 'this month', 'next month', 'this year', 'next year'];
      const foundTimeline = timelineKeywords.some(keyword => lowerMessage.includes(keyword));
      if (foundTimeline) {
        const timelineText = timelineKeywords.find(keyword => lowerMessage.includes(keyword)) || 'unspecified timeline';
        updatedFields.timeline = timelineText;
        extractedInfo.push(`Timeline: ${timelineText}`);
      } else if (lowerMessage.includes('by') || lowerMessage.includes('before')) {
        updatedFields.timeline = message.split(/by|before/i)[1]?.trim() || 'unspecified timeline';
        extractedInfo.push(`Timeline: ${updatedFields.timeline}`);
      }
    }
    
    // Check for specific requirements
    if (lowerMessage.includes('require') || lowerMessage.includes('need') || lowerMessage.includes('want') || lowerMessage.includes('must') || lowerMessage.includes('should')) {
      const requirementText = message.split(/require|need|want|must|should/i)[1]?.trim() || 'unspecified requirement';
      updatedFields.specificRequirements = [...(updatedFields.specificRequirements || []), requirementText];
      extractedInfo.push(`Requirement: ${requirementText}`);
    }
    
    // Check for property details if not already set or if a property is not selected
    if (!updatedFields.propertyDetails && !selectedProperty && (lowerMessage.includes('property') || lowerMessage.includes('home') || lowerMessage.includes('house') || lowerMessage.includes('address'))) {
      updatedFields.propertyDetails = message;
      extractedInfo.push(`Property Details: ${message}`);
    }
    
    // Determine the next question to ask
    let nextQuestion = '';
    if (!updatedFields.budget) {
      nextQuestion = 'Can you tell me about your budget for this project?';
    } else if (!updatedFields.timeline) {
      nextQuestion = 'What is your desired timeline for completing this project?';
    } else if (!updatedFields.specificRequirements || updatedFields.specificRequirements.length === 0) {
      nextQuestion = 'Do you have any specific requirements or concerns for this project?';
    } else {
      nextQuestion = 'Is there anything else you would like to add to your bid request before I compile it?';
    }
    
    return {
      updatedFields,
      extractedInfo,
      nextQuestion
    };
  };
  
  // Function to generate AI response based on analysis
  const generateAIResponse = (analysis: { updatedFields: Partial<typeof editableFields>, extractedInfo: string[], nextQuestion: string }) => {
    let response = 'Thank you for providing that information. ';
    
    if (analysis.extractedInfo.length > 0) {
      response += `I've noted the following details:\n\n`;
      analysis.extractedInfo.forEach((info, index) => {
        response += `${index + 1}. ${info}\n`;
      });
      response += `\n`;
    }
    
    if (analysis.nextQuestion) {
      response += analysis.nextQuestion;
    } else {
      response += 'I believe I have all the necessary information to create your bid request. Shall I compile it now?';
    }
    
    return response;
  };

  const toggleListening = () => {
    setIsListening(!isListening);
    // Implement speech recognition logic here if needed
  };

  const generatePDF = async (saveFile = true) => {
    if (!pdfContentRef.current) return null;
    
    try {
      setIsLoading(true);
      const content = pdfContentRef.current;
      
      // Dynamically import html2canvas
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      
      const canvas = await html2canvas(content, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Dynamically import jsPDF
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;
      
      // Create a new jsPDF instance with the correct syntax for version 3.0.1
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Add metadata to the PDF
      pdf.setProperties({
        title: `Bid Request - ${projectType || 'Home Hardening Project'}`,
        subject: 'Request for Bid',
        creator: 'SafeHaven.chat',
        author: 'SafeHaven.chat'
      });

      // Calculate dimensions
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      
      // If content spans multiple pages, add additional pages
      if (imgHeight > pageHeight) {
        let remainingHeight = imgHeight;
        let position = -pageHeight; // Starting position for the second page
        
        while (remainingHeight > pageHeight) {
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          
          remainingHeight -= pageHeight;
          position -= pageHeight;
        }
      }
      
      if (saveFile) {
        // Save the PDF
        pdf.save(`bid-request-${projectType ? projectType.toLowerCase().replace(/\s+/g, '-') : 'project'}-${new Date().toISOString().split('T')[0]}.pdf`);
      }
      
      return pdf;
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF. Please try again.');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePdfPreview = () => {
    setPdfPreviewOpen(true);
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPreviewMode('view'); // Reset to view mode when closing
  };
  
  const handlePreviewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'view' | 'edit' | null) => {
    if (newMode !== null) {
      setPreviewMode(newMode);
      
      // If switching to edit mode, initialize editable fields with current values
      if (newMode === 'edit') {
        const extractedInfo = extractProjectInfo();
        setEditableFields({
          projectOverview: `This document contains specifications for a home hardening project designed to improve wildfire resilience. 
The project focuses on ${projectType ? projectType.toLowerCase() : 'various home hardening measures'} 
to enhance the property's ability to withstand wildfire threats.`,
          budget: extractedInfo.budget,
          timeline: extractedInfo.timeline,
          propertyDetails: extractedInfo.propertyDetails,
          projectRequirements: 'The contractor will be responsible for providing all necessary materials, labor, and equipment to complete the project according to industry standards and local building codes.',
          specificRequirements: extractedInfo.specificRequirements,
          contactInfo: extractedInfo.contactInfo
        });
      }
    }
  };

  const createEmailWithPdf = async () => {
    try {
      setIsLoading(true);
      const pdf = await generatePDF(false);
      
      if (!pdf) {
        throw new Error('Failed to generate PDF');
      }
      
      // Create the email subject
      const subject = encodeURIComponent(`Bid Request - ${projectType || 'Home Hardening Project'}`);
      
      // Create the email body
      const body = encodeURIComponent(
        `Please find attached the bid request for my home hardening project.\n\n` +
        `Project Type: ${projectType || 'Home Hardening Project'}\n` +
        `Date: ${new Date().toLocaleDateString()}\n\n` +
        `This bid request was generated using SafeHaven.chat.`
      );
      
      // Create a mailto link with the PDF attachment
      // Note: Email clients have varying support for data URI attachments
      // This approach works in some clients but not all
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
      
      // Inform the user about potential limitations
      alert('An email draft has been created. Please note that attaching files via mailto links is not supported by all email clients. If the PDF is not attached, please download it separately and attach it to your email.');
    } catch (error) {
      console.error('Error creating email with PDF:', error);
      alert('There was an error creating the email. Please try downloading the PDF and attaching it manually.');
    } finally {
      setIsLoading(false);
    }
  };

  // Define interface for project information
  interface ProjectInfo {
    budget: string;
    timeline: string;
    propertyDetails: string;
    specificRequirements: string[];
    contactInfo: string;
  }

  // Extract key information from chat messages
  const extractProjectInfo = (): ProjectInfo => {
    const userMessages = chatMessages.filter(msg => msg.role === 'user');
    
    // Initialize with default values
    const info: ProjectInfo = {
      budget: 'Not specified',
      timeline: 'Not specified',
      propertyDetails: 'Not specified',
      specificRequirements: [],
      contactInfo: 'Not specified'
    };
    
    // Extract information from messages
    userMessages.forEach(msg => {
      const content = msg.content.toLowerCase();
      
      // Budget information
      if (content.includes('budget') || content.includes('cost') || content.includes('$') || content.includes('dollar')) {
        info.budget = msg.content;
      }
      
      // Timeline information
      if (content.includes('timeline') || content.includes('schedule') || content.includes('deadline') || 
          content.includes('complete by') || content.includes('finish by')) {
        info.timeline = msg.content;
      }
      
      // Property details
      if (content.includes('square foot') || content.includes('sq ft') || content.includes('property') || 
          content.includes('home is') || content.includes('house is') || content.includes('built in')) {
        info.propertyDetails = msg.content;
      }
      
      // Specific requirements
      if (content.includes('need') || content.includes('want') || content.includes('require') || 
          content.includes('must have') || content.includes('important')) {
        info.specificRequirements.push(msg.content);
      }
      
      // Contact information
      if (content.includes('contact') || content.includes('email') || content.includes('phone') || 
          content.includes('call me') || content.includes('@')) {
        info.contactInfo = msg.content;
      }
    });
    
    return info;
  };

  // Format the chat messages into a structured bid request document
  const formatBidRequest = () => {
    const projectInfo = projectType || 'Home Hardening Project';
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    
    // Extract additional project information
    const extractedInfo = extractProjectInfo();
    
    // If in edit mode, use the editable fields values
    const displayData = previewMode === 'edit' ? {
      ...extractedInfo,
      budget: editableFields.budget || extractedInfo.budget,
      timeline: editableFields.timeline || extractedInfo.timeline,
      propertyDetails: editableFields.propertyDetails || extractedInfo.propertyDetails,
      contactInfo: editableFields.contactInfo || extractedInfo.contactInfo
    } : extractedInfo;
    
    // Create a structured document from the conversation
    return (
      <Box sx={{ p: 4, bgcolor: 'background.paper', color: 'text.primary' }}>
        {/* Header with logo placeholder */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            SafeHaven
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Home Hardening
          </Typography>
        </Box>
        
        {/* Title */}
        <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
            Request for Bid
          </Typography>
          <Typography variant="h5" gutterBottom align="center" color="primary.main">
            {projectInfo}
          </Typography>
          <Typography variant="body2" gutterBottom align="center" color="text.secondary">
            Created on {date}
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Project Overview */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Project Overview
          </Typography>
          {previewMode === 'edit' ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editableFields.projectOverview}
              onChange={(e) => setEditableFields({...editableFields, projectOverview: e.target.value})}
              variant="outlined"
              margin="normal"
            />
          ) : (
            <Typography variant="body1" paragraph>
              {editableFields.projectOverview || `This document contains specifications for a home hardening project designed to improve wildfire resilience. 
The project focuses on ${projectType ? projectType.toLowerCase() : 'various home hardening measures'} 
to enhance the property's ability to withstand wildfire threats.`}
            </Typography>
          )}
        </Box>
        
        {/* Budget Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Budget Information
          </Typography>
          {previewMode === 'edit' ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editableFields.budget}
              onChange={(e) => setEditableFields({...editableFields, budget: e.target.value})}
              variant="outlined"
              margin="normal"
              placeholder="Enter budget information"
            />
          ) : (
            <Typography variant="body1">
              {displayData.budget !== 'Not specified' ? displayData.budget : 'Budget details will be discussed upon contractor selection.'}
            </Typography>
          )}
        </Box>
        
        {/* Desired Timeline */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Desired Timeline
          </Typography>
          {previewMode === 'edit' ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editableFields.timeline}
              onChange={(e) => setEditableFields({...editableFields, timeline: e.target.value})}
              variant="outlined"
              margin="normal"
              placeholder="Enter timeline information"
            />
          ) : (
            <Typography variant="body1">
              {displayData.timeline !== 'Not specified' ? displayData.timeline : 'Project timeline to be determined based on contractor availability and project scope.'}
            </Typography>
          )}
        </Box>
        
        {/* Property Details */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Property Details
          </Typography>
          {previewMode === 'edit' ? (
            <TextField
              fullWidth
              multiline
              rows={3}
              value={editableFields.propertyDetails}
              onChange={(e) => setEditableFields({...editableFields, propertyDetails: e.target.value})}
              variant="outlined"
              margin="normal"
              placeholder="Enter property details"
            />
          ) : (
            <Typography variant="body1">
              {displayData.propertyDetails !== 'Not specified' ? displayData.propertyDetails : 'Detailed property information will be provided during the site visit.'}
            </Typography>
          )}
        </Box>
        
        {/* Project Requirements */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Project Requirements
          </Typography>
          {previewMode === 'edit' ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              value={editableFields.projectRequirements}
              onChange={(e) => setEditableFields({...editableFields, projectRequirements: e.target.value})}
              variant="outlined"
              margin="normal"
              placeholder="Enter project requirements"
            />
          ) : (
            <Typography variant="body1">
              {editableFields.projectRequirements || 'The contractor will be responsible for providing all necessary materials, labor, and equipment to complete the project according to industry standards and local building codes.'}
            </Typography>
          )}
        </Box>
        
        {/* Specific Requirements */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Specific Requirements
          </Typography>
          {previewMode === 'edit' ? (
            <Box>
              {editableFields.specificRequirements.map((req, index) => (
                <TextField
                  key={index}
                  fullWidth
                  value={req}
                  onChange={(e) => {
                    const updatedReqs = [...editableFields.specificRequirements];
                    updatedReqs[index] = e.target.value;
                    setEditableFields({...editableFields, specificRequirements: updatedReqs});
                  }}
                  variant="outlined"
                  margin="normal"
                  placeholder={`Requirement ${index + 1}`}
                />
              ))}
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => {
                  setEditableFields({
                    ...editableFields, 
                    specificRequirements: [...editableFields.specificRequirements, '']
                  });
                }}
                sx={{ mt: 1 }}
              >
                Add Requirement
              </Button>
            </Box>
          ) : (
            <List sx={{ 
              listStyleType: 'disc', 
              pl: 4,
              '& .MuiListItem-root': {
                display: 'list-item',
                py: 0.5
              }
            }}>
              {displayData.specificRequirements.length > 0 ? (
                displayData.specificRequirements.map((req, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={req} />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="Specific requirements will be discussed during the bidding process." />
                </ListItem>
              )}
            </List>
          )}
        </Box>
        
        {/* Contact Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Contact Information
          </Typography>
          {previewMode === 'edit' ? (
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editableFields.contactInfo}
              onChange={(e) => setEditableFields({...editableFields, contactInfo: e.target.value})}
              variant="outlined"
              margin="normal"
              placeholder="Enter contact information"
            />
          ) : (
            <Typography variant="body1">
              {displayData.contactInfo !== 'Not specified' 
                ? displayData.contactInfo 
                : 'Please submit your bid by email with "Bid for ' + projectInfo + '" in the subject line.'}
            </Typography>
          )}
        </Box>
        
        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Generated by SafeHaven
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This document is intended for bid solicitation purposes only.
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      maxHeight: '100vh',
      overflow: 'hidden'
    }}>
      <Paper 
        elevation={0}
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto',
          overscrollBehavior: 'none'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Home Hardening Bid Request Tool
          </Typography>
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<PictureAsPdfIcon />}
              onClick={handlePdfPreview}
            >
              Preview
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2, width: '100%' }}>
          <FormControl fullWidth size="small">
            <InputLabel id="project-type-label">Project Type</InputLabel>
            <Select
              labelId="project-type-label"
              id="project-type-select"
              value={projectType}
              label="Project Type"
              onChange={handleProjectTypeChange}
            >
              <MenuItem value="">
                <em>Select a project type</em>
              </MenuItem>
              {projectTypes.map(type => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small" disabled={propertiesLoading || properties.length === 0}>
            <InputLabel id="property-label">Property</InputLabel>
            <Select
              labelId="property-label"
              id="property-select"
              value={selectedProperty}
              label="Property"
              onChange={handlePropertyChange}
            >
              <MenuItem value="">
                <em>{properties.length === 0 ? 'No saved properties' : 'Select a property'}</em>
              </MenuItem>
              {properties.map((property: Property) => (
                <MenuItem key={property.id} value={property.id}>{property.address}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}>
          {/* Chat interface */}
          <Box
            ref={chatEndRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              mb: 2,
              p: 2,
              backgroundColor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              minHeight: 0
            }}
          >
            {chatMessages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: message.role === 'assistant' ? '#F5F8FB' : 'background.paper',
                  borderRadius: 2,
                  maxWidth: '100%'
                }}
              >
                <MarkdownContainer>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                </MarkdownContainer>
              </Box>
            ))}
          </Box>

          {/* Input area */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (userInput.trim() && !isLoading) {
                    handleSendMessage(userInput);
                  }
                }
              }}
              placeholder="Describe your project needs..."
              variant="outlined"
              disabled={isLoading}
            />
            <IconButton
              color="primary"
              onClick={() => handleSendMessage(userInput)}
              disabled={isLoading || !userInput.trim()}
              sx={{ alignSelf: 'flex-end' }}
            >
              {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
            <IconButton
              color={isListening ? 'secondary' : 'primary'}
              onClick={toggleListening}
              sx={{ alignSelf: 'flex-end' }}
            >
              {isListening ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
          </Box>
        </Box>
      </Paper>
      
      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={closePdfPreview}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Bid Request Preview</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <ToggleButtonGroup
                value={previewMode}
                exclusive
                onChange={handlePreviewModeChange}
                size="small"
                sx={{ mr: 2 }}
              >
                <ToggleButton value="view">View</ToggleButton>
                <ToggleButton value="edit">Edit</ToggleButton>
              </ToggleButtonGroup>
              
              <Button 
                variant="contained" 
                startIcon={<EmailIcon />}
                onClick={createEmailWithPdf}
                disabled={isLoading || previewMode === 'edit'}
              >
                Email PDF
              </Button>
              <Button 
                variant="contained" 
                startIcon={<DownloadIcon />}
                onClick={() => generatePDF(true)}
                disabled={isLoading || previewMode === 'edit'}
              >
                Download PDF
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ overflow: 'auto' }}>
            <div ref={pdfContentRef}>
              {formatBidRequest()}
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          {previewMode === 'edit' && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => setPreviewMode('view')}
            >
              Save Changes
            </Button>
          )}
          <Button onClick={closePdfPreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BidRequestTool;
